import React, { useState } from 'react';
import { Box, Typography, ButtonBase, Collapse, Chip } from '@mui/material';
import { SVG } from 'components/Asset/SVG';
import { formatNumberToShortScale } from 'views/Production/Hauler/component/utils';
import { capitalizeWords } from './utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const criticalityAndRiskColorMap = {
  eol: '#EB5757',
  high: '#EB5757',
  med: '#FFA403',
  low: '#009161',
} as const;

type Criticality = keyof typeof criticalityAndRiskColorMap;

const DetailsComponent = ({
  enableDarkTheme,
  consequences,
  actionRecommended,
  component,
  subComponent,
  iconName,
  age,
  maintenanceType,
  risk,
  criticality,
  recommendationText,
}: {
  enableDarkTheme: boolean;
  isPlanned: boolean;
  consequences: string[];
  actionRecommended: boolean;
  component: string;
  subComponent: string;
  iconName: string;
  age: number | string;
  squaresRecommendedDate: string;
  maintenanceType: string;
  risk: string;
  criticality: string;
  recommendationText: string;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const fontColor = palette.textPrimary;

  const criticalityColor =
    criticalityAndRiskColorMap[criticality as Criticality] ?? '#009161';
  const riskColor =
    criticalityAndRiskColorMap[risk as Criticality] ?? '#009161';

  return (
    <ButtonBase
      component="div"
      onClick={() => {
        setIsExpanded(!isExpanded);
      }}
      sx={{
        display: 'block',
        textAlign: 'left',
        width: 'calc(100% - 20px)',
        margin: '10px',
        borderRadius: '4px',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          backgroundColor: palette.paperBackground,
          padding: '16px 20px',
          borderRadius: '4px',
          borderLeft: `4px solid ${
            isExpanded ? palette.primary : criticalityColor
          }`,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.4)',
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: isExpanded ? 2 : 0,
          }}
        >
          <Box
            sx={{
              backgroundColor: palette.boxBackground,
              padding: '4px',
              borderRadius: '8px',
              display: 'flex',
              mr: 2,
            }}
          >
            <SVG name={iconName} />
          </Box>

          <Box sx={{ flexGrow: 1 }}>
            <Typography
              sx={{ fontWeight: '600', color: fontColor, fontSize: '14px' }}
            >
              {capitalizeWords(component)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: palette.grey, fontFamily: 'monospace' }}
            >
              {formatNumberToShortScale(Number(age) * 24)} {t('hrs_2')}{' '}
              {capitalizeWords(subComponent)}
            </Typography>
          </Box>

          <SVG
            name="priority"
            style={{
              fill: riskColor,
            }}
          />
        </Box>
        {maintenanceType && (
          <Box
            sx={{
              marginTop: '5px',
            }}
          >
            <Chip
              label={capitalizeWords(maintenanceType)}
              sx={{
                borderRadius: '4px',
                backgroundColor: '#5D5D5D',
                fontSize: '12px',
                fontWeight: '700',
                height: 'auto',
                '.MuiChip-label': {
                  padding: '4px 10px',
                },
                color: '#FFFFFF',
              }}
            />
          </Box>
        )}

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              borderTop: `1px solid ${palette.boxBackground}`,
              pt: 2,
              mt: 1,
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: palette.grey,
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
              >
                Frequency
              </Typography>
              <Typography
                sx={{
                  color: fontColor,
                  fontFamily: 'monospace',
                  mt: 0.5,
                  fontSize: '14px',
                }}
              >
                {recommendationText}
              </Typography>
            </Box>

            <Box
              sx={{
                width: '80px',
                height: '45px',
                backgroundColor: palette.paperBackground,
                borderRadius: '4px',
              }}
            />
          </Box>
          {consequences.length > 0 && (
            <Box display={'flex'} flexDirection={'column'} gap={0}>
              <Typography
                sx={{
                  color: palette.grey,
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
              >
                Consequences
              </Typography>
              {consequences.map((item, index) => {
                return (
                  <Typography
                    key={index}
                    sx={{
                      color: fontColor,
                      fontFamily: 'monospace',
                      mt: 0.5,
                      fontSize: '14px',
                    }}
                  >
                    {item}
                  </Typography>
                );
              })}
            </Box>
          )}
        </Collapse>
      </Box>
    </ButtonBase>
  );
};

export default DetailsComponent;
